import React, { useState, useEffect } from 'react';
import { Users, Search, ChevronDown, Package, X, Mail, Phone, MapPin } from 'lucide-react';
import { api } from '../../services/api';
import './AdminUI.css';
import AdminTopbarPortal from './AdminTopbarPortal';

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailTab, setDetailTab] = useState('orders');
  const [sortBy, setSortBy] = useState('recent');
  const [page, setPage] = useState(1);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [summary, setSummary] = useState({
    customerCount: 0,
    validOrderCount: 0,
    validRevenue: 0,
    averageOrderValue: 0,
  });
  const pageSize = 12;

  const load = async (requestedPage = page) => {
    try {
      const data = await api.getAdminCustomers({
        page: requestedPage,
        pageSize,
        search: searchQuery.trim() || undefined,
        sort: sortBy,
      });
      setCustomers(data.items || []);
      setTotalCustomers(data.total || 0);
      setTotalPages(data.totalPages || 1);
      if (data.summary) setSummary(data.summary);
    } catch (err) {
      console.error('Failed to load customers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => load(page), searchQuery ? 300 : 0);
    return () => clearTimeout(timer);
  }, [page, searchQuery, sortBy]);

  const loadCustomerDetails = async (customerId) => {
    if (expandedCustomer === customerId) {
      setExpandedCustomer(null);
      setCustomerDetails(null);
      return;
    }
    setExpandedCustomer(customerId);
    setDetailTab('orders');
    setLoadingDetails(true);
    try {
      const data = await api.getAdminCustomer(customerId, { page: 1, pageSize: 10 });
      setCustomerDetails(data);
    } catch (err) {
      console.error('Failed to load customer details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const visibleCustomers = customers;
  const changePage = (nextPage) => {
    setPage(nextPage);
    setExpandedCustomer(null);
    setCustomerDetails(null);
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
  };

  useEffect(() => { setPage(1); setExpandedCustomer(null); }, [searchQuery, sortBy]);

  if (loading) return <div className="flex justify-center py-16"><div className="w-7 h-7 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="admin-view space-y-4">
      <AdminTopbarPortal><div className="admin-topbar-controls">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Customers</h2>
          <p className="text-gray-500 text-sm">{totalCustomers} matching customers</p>
        </div>
        <div className="admin-toolbar">
          <div className="admin-search-field">
            <Search className="w-4 h-4" />
            <input type="text" placeholder="Search customers..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
          </div>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} aria-label="Sort customers">
            <option value="recent">Recently added</option>
            <option value="spent">Highest spend</option>
            <option value="orders">Most orders</option>
            <option value="name">Name A–Z</option>
          </select>
          <span className="admin-result-count">{totalCustomers} results</span>
        </div>
      </div></AdminTopbarPortal>

      <div className="admin-metric-grid">
        <div><span>Customers</span><strong>{summary.customerCount || 0}</strong></div>
        <div><span>Total orders</span><strong>{summary.validOrderCount || 0}</strong></div>
        <div><span>Average order value</span><strong>£{Number(summary.averageOrderValue || 0).toFixed(2)}</strong></div>
      </div>

      {customers.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="font-semibold text-gray-900 mb-1">{searchQuery ? 'No customers found' : 'No customers yet'}</h3>
          <p className="text-gray-500 text-sm">{searchQuery ? 'Try a different search term' : 'Customer data will appear here once they register'}</p>
        </div>
      ) : (
        <div className="admin-customers-table-shell">
          <table className="admin-customers-table">
            <thead><tr><th>Customer</th><th>Email</th><th>Contact number</th><th>Orders</th><th className="admin-customers-spend-heading">Total spent</th><th aria-label="Customer details" /></tr></thead>
            <tbody>
              {visibleCustomers.map(customer => (
                <tr key={customer.id} className="admin-customer-table-row" onClick={() => loadCustomerDetails(customer.id)}>
                  <td data-label="Customer">
                    <div className="admin-customer-table-identity">
                      <div className="admin-customer-avatar">{customer.name?.charAt(0).toUpperCase() || 'U'}</div>
                      <strong>{customer.name || 'Unknown'}</strong>
                    </div>
                  </td>
                  <td data-label="Email">{customer.email || 'No email'}</td>
                  <td data-label="Contact number">{customer.phone || '—'}</td>
                  <td data-label="Orders"><span className="admin-customer-order-pill"><Package className="w-3 h-3" /> {customer.orderCount || 0} {(customer.orderCount || 0) === 1 ? 'order' : 'orders'}</span></td>
                  <td data-label="Total spent" className="admin-customer-table-spend"><strong>£{Number(customer.totalSpent || 0).toFixed(2)}</strong></td>
                  <td className="admin-customer-table-toggle"><ChevronDown className="w-5 h-5 text-gray-400" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {expandedCustomer && (
        <div className="admin-drawer-backdrop" onMouseDown={() => { setExpandedCustomer(null); setCustomerDetails(null); }}>
          <aside className="admin-drawer admin-customer-drawer" onMouseDown={event => event.stopPropagation()} aria-label="Customer details">
            <div className="admin-drawer-heading">
              <div><span>Customer</span><h3>{customerDetails?.name || 'Customer details'}</h3></div>
              <button onClick={() => { setExpandedCustomer(null); setCustomerDetails(null); }} aria-label="Close customer details"><X /></button>
            </div>
            {loadingDetails ? (
              <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" /></div>
            ) : customerDetails ? (
              <>
                <div className="admin-customer-drawer-contact">
                  <div className="admin-customer-avatar">{customerDetails.name?.charAt(0).toUpperCase() || 'U'}</div>
                  <div><strong>{customerDetails.name || 'Unknown'}</strong><span><Mail />{customerDetails.email || 'No email'}</span>{customerDetails.phone && <span><Phone />{customerDetails.phone}</span>}</div>
                </div>
                <div className="admin-customer-drawer-stats">
                  <div><span>Orders</span><strong>{customerDetails.orderCount || 0}</strong></div>
                  <div><span>Total spent</span><strong>£{Number(customerDetails.totalSpent || 0).toFixed(2)}</strong></div>
                  <div><span>Average order</span><strong>£{Number(customerDetails.averageOrderValue || 0).toFixed(2)}</strong></div>
                </div>
                <div className="grid grid-cols-2 gap-1 rounded-xl bg-gray-100 p-1 mt-4">
                  <button onClick={() => setDetailTab('orders')}
                    className={`rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${detailTab === 'orders' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                    Recent orders
                  </button>
                  <button onClick={() => setDetailTab('addresses')}
                    className={`rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${detailTab === 'addresses' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                    Delivery addresses
                  </button>
                </div>

                {detailTab === 'orders' && (
                  <>
                    <div className="admin-customer-orders-heading"><h4>Recent orders</h4><span>{customerDetails.orderCount || 0} total</span></div>
                    {customerDetails.orders?.length ? (
                      <div className="admin-customer-order-list">
                        {customerDetails.orders.map(order => (
                          <div key={order.id} className="admin-customer-order-row">
                            <div><strong>Order #{order.orderNumber || order.id.slice(0, 8)}</strong><span>{new Date(order.createdAt).toLocaleDateString('en-GB')} · {order.items?.length || 0} items</span></div>
                            <div><strong>£{Number(order.total || 0).toFixed(2)}</strong><span className="capitalize">{order.paymentStatus === 'refunded' ? 'Refunded' : order.status}</span></div>
                          </div>
                        ))}
                      </div>
                    ) : <div className="admin-empty-state">No qualifying orders yet.</div>}
                  </>
                )}

                {detailTab === 'addresses' && (
                  <>
                    <div className="admin-customer-orders-heading"><h4>Saved delivery addresses</h4><span>{customerDetails.addressCount || 0} total</span></div>
                    {customerDetails.addresses?.length ? (
                      <div className="space-y-2">
                        {customerDetails.addresses.map(address => (
                          <div key={address.id} className="rounded-xl border border-gray-200 bg-white p-3 flex items-start gap-3">
                            <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0"><MapPin className="w-4 h-4 text-gray-500" /></div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2"><strong className="text-sm text-gray-900">{address.label || 'Saved address'}</strong>{address.isDefault && <span className="text-[10px] font-semibold uppercase tracking-wide text-green-700 bg-green-50 rounded-full px-2 py-0.5">Default</span>}</div>
                              <p className="text-sm text-gray-600 mt-1">{address.line1}{address.line2 ? `, ${address.line2}` : ''}</p>
                              <p className="text-sm text-gray-500">{address.city}, {address.postcode}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : <div className="admin-empty-state">No saved delivery addresses.</div>}
                  </>
                )}
              </>
            ) : <div className="admin-empty-state">Customer details could not be loaded.</div>}
          </aside>
        </div>
      )}
      {totalPages > 1 && (
        <div className="admin-pagination">
          <button disabled={page === 1} onClick={() => changePage(page - 1)}>Previous</button>
          <span>Page {page} of {totalPages}</span>
          <button disabled={page === totalPages} onClick={() => changePage(page + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}
